import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { User, Building, Briefcase, Target, CheckCircle, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CustomerProfile = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [completion, setCompletion] = useState({ percentage: 0, completed: false });
  
  const [profile, setProfile] = useState({
    contact_person: '',
    contact_email: '',
    phone: '',
    mobile: '',
    company_name: '',
    company_website: '',
    company_address: '',
    city: '',
    state: '',
    zip_code: '',
    industry: '',
    business_type: '',
    years_in_business: '',
    company_size: '',
    primary_needs: [],
    project_frequency: 'Occasional',
    typical_project_size: '',
    budget_range: '',
    how_did_you_hear: '',
    special_requirements: '',
    preferred_contact_method: 'Email'
  });

  useEffect(() => {
    loadProfile();
    loadCompletion();
  }, []);

  const loadProfile = async () => {
    try {
      const response = await axios.get(`${API}/customer/profile`);
      setProfile(response.data);
    } catch (error) {
      console.error('Failed to load profile', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCompletion = async () => {
    try {
      const response = await axios.get(`${API}/customer/profile/completion`);
      setCompletion(response.data);
    } catch (error) {
      console.error('Failed to load completion status');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await axios.post(`${API}/customer/profile`, profile);
      toast.success('Profile saved successfully!');
      loadCompletion();
      
      if (currentStep < 4) {
        setCurrentStep(currentStep + 1);
      } else {
        navigate('/portal');
      }
    } catch (error) {
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleNeedToggle = (need) => {
    const needs = profile.primary_needs || [];
    if (needs.includes(need)) {
      setProfile({ ...profile, primary_needs: needs.filter(n => n !== need) });
    } else {
      setProfile({ ...profile, primary_needs: [...needs, need] });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-primary text-xl font-mono uppercase tracking-widest">Loading...</div>
      </div>
    );
  }

  const steps = [
    { number: 1, title: 'Contact Info', icon: User },
    { number: 2, title: 'Company Info', icon: Building },
    { number: 3, title: 'Business Details', icon: Briefcase },
    { number: 4, title: 'Service Needs', icon: Target }
  ];

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/portal')}
            className="mb-4 font-mono uppercase text-xs"
          >
            <ArrowLeft size={14} className="mr-2" />
            Back to Portal
          </Button>
          
          <h1 className="text-4xl md:text-5xl font-black tracking-tight uppercase text-foreground mb-2">
            MY PROFILE
          </h1>
          <p className="text-sm font-mono uppercase tracking-wide text-muted-foreground">
            Complete your profile to get personalized service
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-mono uppercase text-muted-foreground">Profile Completion</span>
            <span className="text-sm font-bold font-mono text-primary">{completion.percentage}%</span>
          </div>
          <div className="w-full bg-border rounded-full h-3">
            <div 
              className="bg-primary rounded-full h-3 transition-all duration-500"
              style={{ width: `${completion.percentage}%` }}
            />
          </div>
        </div>

        {/* Step Indicators */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isActive = currentStep === step.number;
              const isCompleted = currentStep > step.number;
              
              return (
                <React.Fragment key={step.number}>
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-12 h-12 rounded-full flex items-center justify-center border-2 transition-all ${
                        isCompleted
                          ? 'bg-green-500 border-green-500'
                          : isActive
                          ? 'bg-primary border-primary'
                          : 'bg-background border-border'
                      }`}
                    >
                      {isCompleted ? (
                        <CheckCircle size={24} className="text-white" />
                      ) : (
                        <Icon size={24} className={isActive ? 'text-white' : 'text-muted-foreground'} />
                      )}
                    </div>
                    <span className={`text-xs font-mono uppercase mt-2 ${
                      isActive ? 'text-primary font-bold' : 'text-muted-foreground'
                    }`}>
                      {step.title}
                    </span>
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-2 ${
                      currentStep > step.number ? 'bg-green-500' : 'bg-border'
                    }`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Form */}
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="text-2xl font-bold uppercase">
              Step {currentStep}: {steps[currentStep - 1].title}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Step 1: Contact Information */}
              {currentStep === 1 && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="contact_person">Contact Person *</Label>
                      <Input
                        id="contact_person"
                        value={profile.contact_person}
                        onChange={(e) => setProfile({ ...profile, contact_person: e.target.value })}
                        placeholder="John Doe"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="contact_email">Email *</Label>
                      <Input
                        id="contact_email"
                        type="email"
                        value={profile.contact_email}
                        onChange={(e) => setProfile({ ...profile, contact_email: e.target.value })}
                        placeholder="john@company.com"
                        className="font-mono"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="phone">Office Phone</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={profile.phone}
                        onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                        placeholder="(555) 123-4567"
                        className="font-mono"
                      />
                    </div>
                    <div>
                      <Label htmlFor="mobile">Mobile Phone</Label>
                      <Input
                        id="mobile"
                        type="tel"
                        value={profile.mobile}
                        onChange={(e) => setProfile({ ...profile, mobile: e.target.value })}
                        placeholder="(555) 987-6543"
                        className="font-mono"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="preferred_contact_method">Preferred Contact Method *</Label>
                    <select
                      id="preferred_contact_method"
                      value={profile.preferred_contact_method}
                      onChange={(e) => setProfile({ ...profile, preferred_contact_method: e.target.value })}
                      className="w-full mt-1 px-3 py-2 bg-background border border-input rounded-sm"
                      required
                    >
                      <option value="Email">Email</option>
                      <option value="Phone">Phone</option>
                      <option value="Text">Text Message</option>
                    </select>
                  </div>
                </div>
              )}

              {/* Step 2: Company Information */}
              {currentStep === 2 && (
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="company_name">Company Name *</Label>
                    <Input
                      id="company_name"
                      value={profile.company_name}
                      onChange={(e) => setProfile({ ...profile, company_name: e.target.value })}
                      placeholder="ABC Manufacturing Inc."
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="company_website">Company Website</Label>
                    <Input
                      id="company_website"
                      type="url"
                      value={profile.company_website}
                      onChange={(e) => setProfile({ ...profile, company_website: e.target.value })}
                      placeholder="https://www.example.com"
                      className="font-mono"
                    />
                  </div>

                  <div>
                    <Label htmlFor="company_address">Address</Label>
                    <Input
                      id="company_address"
                      value={profile.company_address}
                      onChange={(e) => setProfile({ ...profile, company_address: e.target.value })}
                      placeholder="123 Main Street"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="city">City</Label>
                      <Input
                        id="city"
                        value={profile.city}
                        onChange={(e) => setProfile({ ...profile, city: e.target.value })}
                        placeholder="Springfield"
                      />
                    </div>
                    <div>
                      <Label htmlFor="state">State</Label>
                      <Input
                        id="state"
                        value={profile.state}
                        onChange={(e) => setProfile({ ...profile, state: e.target.value })}
                        placeholder="IL"
                        maxLength={2}
                        className="uppercase"
                      />
                    </div>
                    <div>
                      <Label htmlFor="zip_code">ZIP Code</Label>
                      <Input
                        id="zip_code"
                        value={profile.zip_code}
                        onChange={(e) => setProfile({ ...profile, zip_code: e.target.value })}
                        placeholder="62701"
                        className="font-mono"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Business Details */}
              {currentStep === 3 && (
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="industry">Industry *</Label>
                    <select
                      id="industry"
                      value={profile.industry}
                      onChange={(e) => setProfile({ ...profile, industry: e.target.value })}
                      className="w-full mt-1 px-3 py-2 bg-background border border-input rounded-sm"
                      required
                    >
                      <option value="">Select Industry</option>
                      <option value="Construction">Construction</option>
                      <option value="Manufacturing">Manufacturing</option>
                      <option value="Agriculture">Agriculture</option>
                      <option value="Automotive">Automotive</option>
                      <option value="Energy">Energy & Utilities</option>
                      <option value="Mining">Mining</option>
                      <option value="Transportation">Transportation</option>
                      <option value="Government">Government</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div>
                    <Label htmlFor="business_type">Business Type *</Label>
                    <select
                      id="business_type"
                      value={profile.business_type}
                      onChange={(e) => setProfile({ ...profile, business_type: e.target.value })}
                      className="w-full mt-1 px-3 py-2 bg-background border border-input rounded-sm"
                      required
                    >
                      <option value="">Select Type</option>
                      <option value="Individual">Individual/Sole Proprietor</option>
                      <option value="Small Business">Small Business</option>
                      <option value="Corporation">Corporation</option>
                      <option value="Government">Government/Municipal</option>
                    </select>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="years_in_business">Years in Business</Label>
                      <select
                        id="years_in_business"
                        value={profile.years_in_business}
                        onChange={(e) => setProfile({ ...profile, years_in_business: e.target.value })}
                        className="w-full mt-1 px-3 py-2 bg-background border border-input rounded-sm"
                      >
                        <option value="">Select Range</option>
                        <option value="0-1">Less than 1 year</option>
                        <option value="1-3">1-3 years</option>
                        <option value="3-5">3-5 years</option>
                        <option value="5-10">5-10 years</option>
                        <option value="10+">10+ years</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="company_size">Company Size</Label>
                      <select
                        id="company_size"
                        value={profile.company_size}
                        onChange={(e) => setProfile({ ...profile, company_size: e.target.value })}
                        className="w-full mt-1 px-3 py-2 bg-background border border-input rounded-sm"
                      >
                        <option value="">Select Size</option>
                        <option value="1-10">1-10 employees</option>
                        <option value="11-50">11-50 employees</option>
                        <option value="51-200">51-200 employees</option>
                        <option value="200+">200+ employees</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="how_did_you_hear">How did you hear about us?</Label>
                    <select
                      id="how_did_you_hear"
                      value={profile.how_did_you_hear}
                      onChange={(e) => setProfile({ ...profile, how_did_you_hear: e.target.value })}
                      className="w-full mt-1 px-3 py-2 bg-background border border-input rounded-sm"
                    >
                      <option value="">Select Option</option>
                      <option value="Google Search">Google Search</option>
                      <option value="Referral">Referral</option>
                      <option value="Social Media">Social Media</option>
                      <option value="Advertisement">Advertisement</option>
                      <option value="Trade Show">Trade Show/Event</option>
                      <option value="Previous Customer">Previous Customer</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
              )}

              {/* Step 4: Service Needs */}
              {currentStep === 4 && (
                <div className="space-y-4">
                  <div>
                    <Label className="mb-3 block">Primary Service Needs (Select all that apply) *</Label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {['Welding', 'Fabrication', 'Repair', 'Maintenance', 'Custom Work', 'Emergency Services'].map((need) => (
                        <div
                          key={need}
                          onClick={() => handleNeedToggle(need)}
                          className={`p-4 border rounded-sm cursor-pointer transition-all ${
                            profile.primary_needs?.includes(need)
                              ? 'bg-primary/10 border-primary'
                              : 'bg-background border-border hover:border-primary/50'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                              profile.primary_needs?.includes(need)
                                ? 'bg-primary border-primary'
                                : 'border-border'
                            }`}>
                              {profile.primary_needs?.includes(need) && (
                                <CheckCircle size={16} className="text-white" />
                              )}
                            </div>
                            <span className="font-medium">{need}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="project_frequency">Project Frequency *</Label>
                    <select
                      id="project_frequency"
                      value={profile.project_frequency}
                      onChange={(e) => setProfile({ ...profile, project_frequency: e.target.value })}
                      className="w-full mt-1 px-3 py-2 bg-background border border-input rounded-sm"
                      required
                    >
                      <option value="Occasional">Occasional (1-2 per year)</option>
                      <option value="Quarterly">Quarterly (4-6 per year)</option>
                      <option value="Monthly">Monthly (12+ per year)</option>
                      <option value="Weekly">Weekly (50+ per year)</option>
                      <option value="Daily">Daily/Ongoing</option>
                    </select>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="typical_project_size">Typical Project Size</Label>
                      <select
                        id="typical_project_size"
                        value={profile.typical_project_size}
                        onChange={(e) => setProfile({ ...profile, typical_project_size: e.target.value })}
                        className="w-full mt-1 px-3 py-2 bg-background border border-input rounded-sm"
                      >
                        <option value="">Select Size</option>
                        <option value="Small">Small (Hours)</option>
                        <option value="Medium">Medium (Days)</option>
                        <option value="Large">Large (Weeks)</option>
                        <option value="Enterprise">Enterprise (Months)</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="budget_range">Typical Budget Range</Label>
                      <select
                        id="budget_range"
                        value={profile.budget_range}
                        onChange={(e) => setProfile({ ...profile, budget_range: e.target.value })}
                        className="w-full mt-1 px-3 py-2 bg-background border border-input rounded-sm"
                      >
                        <option value="">Select Range</option>
                        <option value="<$5K">Under $5,000</option>
                        <option value="$5K-$25K">$5,000 - $25,000</option>
                        <option value="$25K-$100K">$25,000 - $100,000</option>
                        <option value="$100K+">$100,000+</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="special_requirements">Special Requirements or Notes</Label>
                    <Textarea
                      id="special_requirements"
                      value={profile.special_requirements}
                      onChange={(e) => setProfile({ ...profile, special_requirements: e.target.value })}
                      placeholder="Any special certifications, safety requirements, or other important information..."
                      className="min-h-[100px]"
                    />
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex gap-3 pt-6 border-t border-border">
                {currentStep > 1 && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setCurrentStep(currentStep - 1)}
                    className="font-mono uppercase"
                  >
                    Previous
                  </Button>
                )}
                <Button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 font-mono uppercase"
                >
                  {saving ? 'Saving...' : currentStep === 4 ? 'Complete Profile' : 'Next Step'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CustomerProfile;
